import { useAuthentication } from '@givebacks/api';
import { ErrorBoundary } from 'core/Components';
import { RouteWithHeaderBackButton } from 'core/Hooks';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { StudentFees } from './StudentFees';
import { StudentFeesNonAuth } from './StudentFeesNonAuth';
import { FeesDetails } from './fees/FeesDetail';
import { FeesList } from './fees/FeesList';
import { Payments } from './students/Payments';
import { Students as StudentsPage } from './students/Students';
import { StudentsList } from './students/StudentsList';
import { UnpaidFees } from './students/UnpaidFees';

/**
 * Guards against unauthenticated users from accessing the student fees page
 * When not authenticated, will show a non-authenticated view allowing the user
 * to login and get redirected to the desired view
 */
const StudentFeesAuthGuard = () => {
  const { isAuthenticated } = useAuthentication();
  return <>{isAuthenticated ? <Outlet /> : <StudentFeesNonAuth />}</>;
};

const StudentFeeRoutes = () => {
  return (
    <Routes>
      <Route
        element={
          <RouteWithHeaderBackButton>
            <Outlet />
          </RouteWithHeaderBackButton>
        }
      >
        <Route path="fee/:feeId" element={<FeesDetails />} />
      </Route>
    </Routes>
  );
};

const StudentRoutes = () => {
  return (
    <Routes>
      <Route
        element={
          <RouteWithHeaderBackButton routeTo="/student-fees/students">
            <StudentsPage />
          </RouteWithHeaderBackButton>
        }
      >
        <Route path="unpaid-fees" element={<UnpaidFees />} />
        <Route path="payments" element={<Payments />} />
        <Route index element={<Navigate to="unpaid-fees" replace />} />
      </Route>
      {/* Catch nested routes to ensure they are rendered outside the layout with the back button */}
      <Route path="unpaid-fees/*" element={<StudentFeeRoutes />} />
      <Route path="payments/*" element={<StudentFeeRoutes />} />
    </Routes>
  );
};

const StudentsFeesRoutes = () => {
  return (
    <Routes>
      <Route
        element={
          <ErrorBoundary.Page title="There was an error loading your student fees">
            <StudentFeesAuthGuard />
          </ErrorBoundary.Page>
        }
      >
        <Route
          path="fees/:feeId"
          element={
            <RouteWithHeaderBackButton routeTo="/student-fees/fees">
              <FeesDetails />
            </RouteWithHeaderBackButton>
          }
        />
        <Route path="students/:studentId/*" element={<StudentRoutes />} />
        {/* This is the layout for the student fees page, views correspond to the necessary subroute displayed via toggle */}
        <Route element={<StudentFees />}>
          <Route path="fees" element={<FeesList />} />
          <Route path="students" element={<StudentsList />} />
        </Route>
        <Route path="*" element={<Navigate to="/student-fees/students" replace />} />
      </Route>
    </Routes>
  );
};

export { StudentsFeesRoutes };
